import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Leaf,
  MapPin,
  Phone,
  Globe,
  Instagram,
  Map as MapIcon,
  Calendar,
  Printer,
  ArrowLeft,
  ExternalLink,
  Copy,
  FileText,
  Building2,
  StickyNote,
  Route,
  Users,
} from "lucide-react";
import { fetchSnapshot } from "../lib/api";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { buildGoogleMapsRouteUrl } from "../lib/routes";
import { useDepot } from "../lib/useDepot";
import { EmailRouteButton } from "../components/EmailRouteButton";
import { toast } from "sonner";

function LeadCard({ d, index }) {
  const directionsUrl =
    d.address &&
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${d.name}, ${d.address}, ${d.city || ""} CA`
    )}`;

  return (
    <div
      data-testid={`lead-card-${d.id}`}
      className="lead-card relative bg-white print:bg-white dark:bg-zinc-900/40 dark:border-zinc-800 border border-zinc-200 rounded-xl p-6 break-inside-avoid"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-[10px] font-mono text-zinc-500 dark:text-zinc-500">
              #{String(index + 1).padStart(2, "0")}
            </span>
            <h3 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              {d.name}
            </h3>
            <ConfidenceBadge value={d.confidence} />
          </div>
          {d.address && (
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1.5 text-sm text-zinc-600 dark:text-zinc-400 hover:text-emerald-600 dark:hover:text-emerald-400"
            >
              <MapPin className="w-3.5 h-3.5" />
              {d.address}
              {d.city ? `, ${d.city}` : ""}
              {d.county ? ` · ${d.county} County` : ""}
            </a>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
            Est. opening
          </div>
          <div className="text-xl font-mono font-semibold text-zinc-900 dark:text-zinc-50 mt-1">
            {d.estimated_opening_date || "—"}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1">
            Phone
          </div>
          {d.phone ? (
            <a
              href={`tel:${d.phone.replace(/[^\d+]/g, "")}`}
              className="text-zinc-900 dark:text-zinc-100 font-mono hover:text-emerald-600 dark:hover:text-emerald-400 inline-flex items-center gap-1.5"
            >
              <Phone className="w-3.5 h-3.5 text-emerald-600" />
              {d.phone}
            </a>
          ) : (
            <span className="text-zinc-400 dark:text-zinc-600 text-xs">
              Not on file
            </span>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1">
            Website
          </div>
          {d.website ? (
            <a
              href={d.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 dark:text-emerald-400 hover:underline truncate inline-flex items-center gap-1 max-w-full"
            >
              <Globe className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                {d.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </span>
            </a>
          ) : (
            <span className="text-zinc-400 dark:text-zinc-600 text-xs">—</span>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1">
            Instagram
          </div>
          {d.instagram_url ? (
            <a
              href={d.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
            >
              <Instagram className="w-3.5 h-3.5" />
              View
            </a>
          ) : (
            <span className="text-zinc-400 dark:text-zinc-600 text-xs">—</span>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1">
            Weedmaps
          </div>
          {d.weedmaps_url ? (
            <a
              href={d.weedmaps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
            >
              <MapIcon className="w-3.5 h-3.5" />
              Listing
            </a>
          ) : (
            <span className="text-zinc-400 dark:text-zinc-600 text-xs">—</span>
          )}
        </div>
      </div>

      {d.confidence_reasoning && (
        <div className="mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1.5">
            Evidence
          </div>
          <p className="text-xs text-zinc-700 dark:text-zinc-300 italic leading-relaxed">
            {d.confidence_reasoning}
          </p>
        </div>
      )}

      {d.sales_notes && (
        <div className="mt-3 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-500/5 border border-emerald-200 dark:border-emerald-500/20">
          <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-700 dark:text-emerald-400 mb-1 flex items-center gap-1">
            <StickyNote className="w-3 h-3" />
            Sales notes
          </div>
          <p className="text-xs text-zinc-800 dark:text-zinc-200 leading-relaxed">
            {d.sales_notes}
          </p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-zinc-500">
        <span>
          {d.city || "—"}
          {d.county ? ` · ${d.county}` : ""}
        </span>
        <span className="hidden print:inline text-zinc-400">
          Action: __________________________________
        </span>
      </div>
    </div>
  );
}

export default function SnapshotView() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [bookendDepot, setBookendDepot] = useState(() => {
    try {
      return localStorage.getItem("jacktrack_bookend_depot") === "1";
    } catch {
      return false;
    }
  });
  const depot = useDepot();

  useEffect(() => {
    fetchSnapshot(id).then(setS);
  }, [id]);

  useEffect(() => {
    try {
      localStorage.setItem("jacktrack_bookend_depot", bookendDepot ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [bookendDepot]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  };

  if (!s) {
    return (
      <div className="min-h-screen bg-zinc-950 px-8 py-10">
        <div className="max-w-[1100px] mx-auto space-y-4">
          <Skeleton className="h-10 w-64 bg-zinc-900/50" />
          <Skeleton className="h-32 bg-zinc-900/50" />
          <Skeleton className="h-32 bg-zinc-900/50" />
        </div>
      </div>
    );
  }

  const counts = (s.dispensaries || []).reduce(
    (acc, d) => {
      acc[d.confidence || "UNKNOWN"] = (acc[d.confidence || "UNKNOWN"] || 0) + 1;
      acc.cities[d.city] = true;
      return acc;
    },
    { cities: {}, HIGH: 0, MEDIUM: 0, LOW: 0, UNKNOWN: 0 }
  );
  const cityCount = Object.keys(counts.cities).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 print:bg-white print:text-zinc-900">
      {/* Toolbar — hidden in print */}
      <div className="sticky top-0 z-20 bg-zinc-950/85 backdrop-blur-md border-b border-zinc-800 print:hidden">
        <div className="max-w-[1100px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link
            to="/snapshots"
            data-testid="back-to-snapshots"
            className="inline-flex items-center gap-1.5 text-xs font-mono text-zinc-500 hover:text-zinc-200"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All lead lists
          </Link>
          <div className="flex items-center gap-2 print:hidden">
            <label
              data-testid="snapshot-detail-bookend-row"
              className="inline-flex items-center gap-1.5 text-[11px] text-zinc-300 cursor-pointer select-none mr-1"
              title={
                depot
                  ? `Start & end at ${depot.address || "depot"}`
                  : "Set the depot in Settings to enable"
              }
            >
              <input
                data-testid="snapshot-detail-bookend-toggle"
                type="checkbox"
                checked={bookendDepot}
                disabled={!depot}
                onChange={(e) => setBookendDepot(e.target.checked)}
                className="h-3.5 w-3.5 accent-emerald-500 disabled:opacity-40"
              />
              Bookend at depot
            </label>
            {(() => {
              const useBookend = bookendDepot && depot;
              const mapUrl = buildGoogleMapsRouteUrl(s.dispensaries || [], {
                depot: useBookend ? depot : null,
              });
              const ids = (s.dispensaries || []).map((d) => d.id).filter(Boolean);
              return (
                <>
                  {mapUrl && (
                    <Button
                      data-testid="snapshot-detail-plan-route"
                      variant="outline"
                      asChild
                      className="border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-100 rounded-md text-xs"
                    >
                      <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                        <Route className="w-3.5 h-3.5 mr-1.5" />
                        Plan route (Maps)
                      </a>
                    </Button>
                  )}
                  {ids.length > 0 && (
                    <Button
                      data-testid="snapshot-detail-view-on-map"
                      variant="outline"
                      asChild
                      className="border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-100 rounded-md text-xs"
                    >
                      <Link to={`/map?ids=${encodeURIComponent(ids.join(","))}`}>
                        <MapIcon className="w-3.5 h-3.5 mr-1.5" />
                        View on map
                      </Link>
                    </Button>
                  )}
                  {mapUrl && (
                    <EmailRouteButton
                      testIdPrefix="snapshot-email"
                      getSubject={() => `JackTrack lead list — ${s.title}`}
                      getMessage={() =>
                        `JackTrack lead list "${s.title}" — ${s.total_count} stops:\n${mapUrl}`
                      }
                    />
                  )}
                </>
              );
            })()}
            <Button
              data-testid="copy-snapshot-link"
              variant="outline"
              onClick={copyLink}
              className="border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-100 rounded-md text-xs"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Copy link
            </Button>
            <Button
              data-testid="print-snapshot"
              onClick={() => window.print()}
              className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md text-xs"
            >
              <Printer className="w-3.5 h-3.5 mr-1.5" />
              Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-[1100px] mx-auto px-6 py-10 print:py-4">
        {/* Cover */}
        <header className="mb-10 print:mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-mono text-emerald-600 dark:text-emerald-400 mb-4">
            <Leaf className="w-3.5 h-3.5" />
            JackTrack · Lead Generator
          </div>
          <h1
            data-testid="snapshot-title"
            className="text-4xl md:text-5xl font-semibold tracking-tight text-zinc-50 print:text-zinc-900 leading-tight"
          >
            {s.title}
          </h1>
          {s.subtitle && (
            <p className="mt-2 text-lg text-zinc-400 print:text-zinc-600">
              {s.subtitle}
            </p>
          )}
          {s.assigned_to && (
            <div
              data-testid="snapshot-detail-assigned"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-sky-300 bg-sky-500/10 border border-sky-500/30 print:text-sky-700 print:bg-transparent print:border-sky-300 px-2 py-1 rounded"
            >
              <Users className="w-3 h-3" />
              Assigned to: {s.assigned_to}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 print:grid-cols-4">
            <div className="border border-zinc-800 print:border-zinc-300 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                Total leads
              </div>
              <div className="text-2xl font-semibold mt-1 print:text-zinc-900">
                {s.total_count}
              </div>
            </div>
            <div className="border border-zinc-800 print:border-zinc-300 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                Cities covered
              </div>
              <div className="text-2xl font-semibold mt-1 print:text-zinc-900">
                {cityCount}
              </div>
            </div>
            <div className="border border-zinc-800 print:border-zinc-300 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                High confidence
              </div>
              <div className="text-2xl font-semibold mt-1 text-emerald-400 print:text-emerald-700">
                {counts.HIGH}
              </div>
            </div>
            <div className="border border-zinc-800 print:border-zinc-300 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                Generated
              </div>
              <div className="text-base font-mono mt-2 print:text-zinc-900">
                {s.created_at?.slice(0, 10)}
              </div>
            </div>
          </div>

          <div className="mt-4 inline-flex items-center flex-wrap gap-2 text-[11px] font-mono text-zinc-500">
            <FileText className="w-3 h-3" />
            <span>Filters:</span>
            {s.filters?.county && (
              <span className="bg-zinc-900 print:bg-zinc-100 border border-zinc-800 print:border-zinc-300 rounded px-2 py-0.5">
                county ={" "}
                {Array.isArray(s.filters.county)
                  ? s.filters.county.join(", ")
                  : s.filters.county}
              </span>
            )}
            {s.filters?.city && (
              <span className="bg-zinc-900 print:bg-zinc-100 border border-zinc-800 print:border-zinc-300 rounded px-2 py-0.5">
                city ={" "}
                {Array.isArray(s.filters.city)
                  ? s.filters.city.join(", ")
                  : s.filters.city}
              </span>
            )}
            {s.filters?.confidence && (
              <span className="bg-zinc-900 print:bg-zinc-100 border border-zinc-800 print:border-zinc-300 rounded px-2 py-0.5">
                confidence = {s.filters.confidence}
              </span>
            )}
            <span className="bg-zinc-900 print:bg-zinc-100 border border-zinc-800 print:border-zinc-300 rounded px-2 py-0.5">
              sort = {s.filters?.sort}
            </span>
            <span className="bg-zinc-900 print:bg-zinc-100 border border-zinc-800 print:border-zinc-300 rounded px-2 py-0.5">
              closed = excluded
            </span>
          </div>
        </header>

        {/* Leads */}
        <section
          data-testid="snapshot-leads"
          className="space-y-4"
        >
          {(s.dispensaries || []).length === 0 ? (
            <div className="py-16 text-center text-zinc-500 print:text-zinc-600">
              <Building2 className="w-10 h-10 mx-auto text-zinc-700 print:text-zinc-300 mb-3" />
              No dispensaries matched these filters at the time the lead list
              was generated.
            </div>
          ) : (
            (s.dispensaries || []).map((d, i) => (
              <LeadCard key={d.id} d={d} index={i} />
            ))
          )}
        </section>

        <footer className="mt-12 pt-6 border-t border-zinc-800 print:border-zinc-300 text-[11px] font-mono text-zinc-500 print:text-zinc-500 flex items-center justify-between flex-wrap gap-2">
          <span>Report ID: {s.id}</span>
          <span>
            Generated by JackTrack · Closed dispensaries excluded · Dates
            evidence-backed
          </span>
        </footer>
      </main>
    </div>
  );
}
